import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AuthCard } from '../../components/AuthLayout';
import { useApi } from '../../auth/AuthProvider';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { Button, Input } from '../../ui';

export function ResetPassword() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(token ? null : t('auth.missingToken'));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFieldError(null);
    if (password !== confirm) {
      setFieldError(t('auth.passwordMismatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.auth.reset({ token, password });
      setDone(true);
      setTimeout(() => navigate('/prijava', { replace: true }), 1500);
    } catch (err) {
      setError(toErrorMessage(t, err).title);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title={t('auth.resetTitle')}
      footer={
        <Link to="/prijava" className="text-accent-dark underline">
          {t('auth.backToLogin')}
        </Link>
      }
    >
      {done ? (
        <p role="status" className="text-[14.5px] text-ink">
          {t('auth.resetDone')}
        </p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <Input
            id="reset-password"
            type="password"
            label={t('auth.password')}
            hint={t('auth.passwordHint')}
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input
            id="reset-confirm"
            type="password"
            label={t('auth.passwordConfirm')}
            autoComplete="new-password"
            required
            error={fieldError ?? undefined}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {error && (
            <p role="alert" className="text-[13px] text-danger">
              {error}
            </p>
          )}
          <Button type="submit" block loading={busy} disabled={!token}>
            {t('auth.resetSubmit')}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
